// Recovers a tab that is still running a previously-cached bundle after a deploy.
//
// The service worker (see registerServiceWorker.js) can keep a returning user on
// the old index.html + main bundle while the server already serves the next
// deploy's hashed assets. When the old bundle then pulls an async chunk (we have
// one: react-json-view via JsonViewer, used by the Settings JSON editor), the
// old webpack runtime and the freshly-served chunk no longer line up. That shows
// up as one of two errors:
//
//   1. The chunk can't be fetched      -> ChunkLoadError / "Loading chunk N failed"
//   2. The chunk loads but plugs a second react-redux into the old runtime, so a
//      connected component can't see the <Provider> -> Invariant Violation:
//      Could not find "store" ... Connect(...). This is the "Uncaught (in
//      promise)" we saw, because react-loadable surfaces it from its import().
//
// In both cases the tab is simply stale, so we unregister the old service worker,
// drop its precache and reload once to pull the current bundle from the network
// (a plain reload would just re-serve the same cached bundle — see
// clearStaleCachesAndReload). A short-lived sessionStorage stamp stops a reload loop if
// the error persists for some other reason (a genuine bug, offline, etc.) — we
// reload at most once per cooldown window, then let the error surface.

const RELOAD_STAMP_KEY = 'bp:stale-bundle-reloaded-at';
const RELOAD_COOLDOWN_MS = 10000;

const isStaleBundleError = (value) => {
  if (!value) return false;
  const name = value.name || '';
  const message = value.message || String(value);

  return (
    name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message) ||
    /could not find "?store"?/i.test(message)
  );
};

const recentlyReloaded = () => {
  try {
    const stamp = Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY));
    return Boolean(stamp) && Date.now() - stamp < RELOAD_COOLDOWN_MS;
  } catch (e) {
    return false;
  }
};

// Tear down the stale service worker and its precache before reloading.
//
// A plain window.location.reload() is served by the still-active old service
// worker straight from its cache — i.e. the *same* stale bundle that just threw.
// So the naive reload never recovers: the error recurs, the cooldown below then
// blocks any further reload, and the tab stays broken until the SW happens to
// update on some later visit. Unregistering the SW and deleting its caches first
// forces the reload to hit the network and pull the current deploy. Best-effort:
// if any step fails or the browser lacks the API, we still reload.
const clearStaleCachesAndReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    }
    if (window.caches && window.caches.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (e) {
    // Nothing we can do here — reload anyway and let the network sort it out.
  }

  window.location.reload();
};

const reloadOnce = () => {
  // We already reloaded a moment ago and the fresh bundle didn't help — this is
  // probably a real error, so stop reloading and let it through.
  if (recentlyReloaded()) return;

  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage can be unavailable (private mode); reload anyway, we just
    // lose the loop protection.
  }

  // Bust the stale service-worker cache before reloading, otherwise the reload
  // just re-serves the same bad bundle (see clearStaleCachesAndReload).
  clearStaleCachesAndReload();
};

// Wires up the global listeners. No-op outside production: in dev the SW isn't
// registered and the stale-chunk mismatch can't happen, and we don't want a real
// "could not find store" bug to trigger reloads while developing.
export default function installStaleBundleGuard() {
  if (process.env.NODE_ENV !== 'production') return;

  // Synchronous throws while rendering a mismatched chunk.
  window.addEventListener('error', (event) => {
    if (isStaleBundleError(event.error || event.message)) {
      reloadOnce();
    }
  });

  // react-loadable's import() rejects here — the "Uncaught (in promise)" path.
  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleBundleError(event.reason)) {
      reloadOnce();
    }
  });
}
